import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Activity, ItineraryDay } from '../../types';
import { stopGroups } from '../../utils/photoSelection';
import Spinner from '../ui/Spinner';
import ConfirmModal from '../ui/ConfirmModal';
import { CalendarDays, Icon, MapPin, Sparkles, Trash2, X } from '../ui/Icon';
import SelectionPopover from './SelectionPopover';
import { DayPickerList, StopPickerList } from './PhotoSelectionPickers';

interface Props {
  count: number;
  allSelected: boolean;
  days: ItineraryDay[];
  busy: boolean;
  onCancel: () => void;
  onToggleAll: () => void;
  onMoveToDay: (dayId: string | null) => void;
  onAssignStop: (stop: Activity | null) => void;
  /** ADR-0058: ask the AI where the selected photos were taken. */
  onSuggestStops: () => void;
  onDelete: () => void;
}

/**
 * What can be done to every selected photo at once. No bulk download on web:
 * R2 serves photos without CORS, so the browser cannot save them as files.
 */
export default function PhotoSelectionBar({ count, allSelected, days, busy, onCancel, onToggleAll, onMoveToDay, onAssignStop, onSuggestStops, onDelete }: Props) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const groups = stopGroups(days);

  return (
    <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 md:px-8 py-3 flex flex-wrap items-center gap-2 animate-fade-in">
      <button onClick={onCancel} className="btn-icon w-8 h-8 text-slate-500" title={t('common.cancel')} aria-label={t('common.cancel')}>
        <Icon as={X} size={16} />
      </button>
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100" aria-live="polite">
        {t('gallery.selection.count', { count })}
      </span>
      <button onClick={onToggleAll} className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer">
        {t(allSelected ? 'gallery.selection.none' : 'gallery.selection.all')}
      </button>
      {busy && <Spinner size="sm" />}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onSuggestStops}
          disabled={busy || groups.length === 0}
          title={t('gallery.suggest.hint')}
          className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-950/40 text-sm font-medium text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
        >
          <Icon as={Sparkles} size={15} />
          {t('gallery.suggest.button')}
        </button>
        <SelectionPopover icon={CalendarDays} label={t('gallery.selection.moveToDay')} disabled={busy}>
          {close => <DayPickerList days={days} onPick={dayId => { close(); onMoveToDay(dayId); }} />}
        </SelectionPopover>
        <SelectionPopover icon={MapPin} label={t('gallery.selection.place')} disabled={busy || groups.length === 0} widthClass="w-80">
          {close => <StopPickerList groups={groups} onPick={stop => { close(); onAssignStop(stop); }} />}
        </SelectionPopover>
        <button
          onClick={() => setConfirming(true)}
          disabled={busy}
          className="btn-icon w-8 h-8 text-red-500 disabled:opacity-50"
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Icon as={Trash2} size={15} />
        </button>
      </div>

      <ConfirmModal
        open={confirming}
        title={t('gallery.selection.deleteConfirm', { count })}
        onConfirm={() => { setConfirming(false); onDelete(); }}
        onCancel={() => setConfirming(false)}
        loading={busy}
      />
    </div>
  );
}
